import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { AlertIcon, BellIcon } from "@/components/ui/icons";
import { formatDateTime } from "@/lib/format";
import { apiErrorMessage } from "@/lib/api-error";
import { cn } from "@/lib/cn";
import type { InboxItem } from "@/types/api";
import { SEVERITY_LABEL, severityTone } from "../campaign-meta";
import { useListInboxQuery } from "../notifications-api";
import {
  internalPath,
  useInboxSync,
  useMarkRead,
  usePushRegistration,
  useUnreadInbox,
} from "../use-inbox";
import { DesktopAlertsDialog } from "./DesktopAlertsDialog";

const PAGE_SIZE = 20;

/**
 * The bell in the top bar: your own inbox.
 *
 * The badge comes from the cheap polled count. The list is fetched only while
 * the popover is open, a page at a time, with "Load more" for older rows.
 * Opening a notice marks it read; there is no separate "mark read" control on
 * each row, because the question the badge answers is "have I seen this".
 *
 * Hand-built like the account menu beside it — outside click and Escape
 * close it, and focus goes back to the bell.
 */
export function InboxMenu() {
  const { unreadItems } = useInboxSync();
  const { signedIn, unreadCount } = useUnreadInbox();
  const { read, readAll, markingAll } = useMarkRead();
  const registerPush = usePushRegistration(signedIn);

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      // Back to the bell, not to the top of the document.
      containerRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const badge = unreadCount > 99 ? "99+" : String(unreadCount);

  return (
    <div ref={containerRef} className="relative">
      <DesktopAlertsDialog enabled={signedIn} onGranted={() => void registerPush()} />
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        aria-label={unreadCount > 0 ? `Notifications, ${badge} unread` : "Notifications"}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={!signedIn}
        onClick={() => setOpen((shown) => !shown)}
      >
        <BellIcon className="size-4.5" />
        {unreadCount > 0 ? (
          <span
            aria-hidden="true"
            className="bg-danger-fill ring-surface tabular absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[0.5625rem] leading-none font-semibold text-white ring-2"
          >
            {badge}
          </span>
        ) : null}
      </Button>

      {open ? (
        <div
          role="dialog"
          aria-label="Notifications"
          className={cn(
            "panel rounded-panel absolute right-0 z-50 mt-2 flex max-h-[min(34rem,calc(100vh-6rem))] flex-col overflow-hidden shadow-lg",
            "w-[min(24rem,calc(100vw-2rem))] animate-rise",
          )}
        >
          <div className="border-border flex items-center justify-between gap-3 border-b px-4 py-2.5">
            <p className="text-content text-sm font-semibold">
              Notifications
              {unreadCount > 0 ? (
                <span className="text-content-subtle ml-1.5 text-xs font-normal">
                  {unreadCount} unread
                </span>
              ) : null}
            </p>
            <Button
              variant="ghost"
              size="sm"
              disabled={unreadCount === 0 || markingAll}
              onClick={() => void readAll(unreadItems)}
            >
              Mark all read
            </Button>
          </div>

          <InboxList onRead={read} onNavigate={() => setOpen(false)} />
        </div>
      ) : null}
    </div>
  );
}

/** Pages of the inbox, one cache entry per cursor, as on the campaign list. */
function InboxList({
  onRead,
  onNavigate,
}: {
  onRead: (item: InboxItem) => void;
  onNavigate: () => void;
}) {
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      {cursors.map((before, index) => (
        <InboxPage
          key={before ?? "first"}
          before={before}
          isFirst={index === 0}
          isLast={index === cursors.length - 1}
          onRead={onRead}
          onNavigate={onNavigate}
          onMore={(cursor) => setCursors((current) => [...current, cursor])}
        />
      ))}
    </div>
  );
}

function InboxPage({
  before,
  isFirst,
  isLast,
  onRead,
  onNavigate,
  onMore,
}: {
  before: string | undefined;
  isFirst: boolean;
  isLast: boolean;
  onRead: (item: InboxItem) => void;
  onNavigate: () => void;
  onMore: (cursor: string) => void;
}) {
  const { data, isLoading, isFetching, isError, error } = useListInboxQuery({
    limit: PAGE_SIZE,
    ...(before ? { before } : {}),
  });

  if (isLoading) {
    return (
      <div className="space-y-2 p-3" role="status" aria-live="polite">
        <span className="sr-only">Loading</span>
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="skeleton h-14 rounded-md" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <p role="alert" className="text-danger px-4 py-3 text-xs">
        {apiErrorMessage(error, "Could not load notifications.")}
      </p>
    );
  }

  const items = data?.items ?? [];

  if (isFirst && items.length === 0) {
    return (
      <p className="text-content-subtle px-4 py-10 text-center text-xs">
        Nothing here yet. Notices from other admins land here.
      </p>
    );
  }

  return (
    <>
      <ul className="divide-border divide-y">
        {items.map((item) => (
          <li key={item.id}>
            <InboxRow item={item} onRead={onRead} onNavigate={onNavigate} />
          </li>
        ))}
      </ul>
      {isLast && data?.next_cursor ? (
        <div className="border-border flex justify-center border-t px-4 py-2.5">
          <Button
            variant="secondary"
            size="sm"
            disabled={isFetching}
            onClick={() => data.next_cursor && onMore(data.next_cursor)}
          >
            Load more
          </Button>
        </div>
      ) : null}
    </>
  );
}

/**
 * One notice. Three treatments, because the API has exactly three severities:
 * info is a plain row, warning is amber, critical is red and also raises the
 * banner in the shell until it is read.
 */
function InboxRow({
  item,
  onRead,
  onNavigate,
}: {
  item: InboxItem;
  onRead: (item: InboxItem) => void;
  onNavigate: () => void;
}) {
  const unread = item.read_at === null;
  const path = internalPath(item.action_url);

  const content = (
    <>
      <span className="flex items-start gap-2">
        {unread ? (
          <span aria-hidden="true" className="bg-brand mt-1.5 size-1.5 shrink-0 rounded-full" />
        ) : (
          <span aria-hidden="true" className="mt-1.5 size-1.5 shrink-0" />
        )}
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span
              className={cn(
                "text-content min-w-0 flex-1 truncate text-[0.8125rem]",
                unread ? "font-semibold" : "font-normal",
              )}
            >
              {unread ? <span className="sr-only">Unread: </span> : null}
              {item.title}
            </span>
            {item.severity !== "info" ? (
              <Badge tone={severityTone(item.severity)} className="shrink-0">
                {SEVERITY_LABEL[item.severity]}
              </Badge>
            ) : null}
          </span>
          <span className="text-content-muted mt-0.5 line-clamp-3 block text-xs leading-relaxed wrap-anywhere">
            {item.body}
          </span>
          <span className="text-content-subtle tabular mt-1 block text-[0.6875rem]">
            {formatDateTime(item.created_at)}
            {path ? " · Open →" : ""}
          </span>
        </span>
      </span>
    </>
  );

  const className = cn(
    "block w-full px-4 py-3 text-left transition-colors",
    item.severity === "critical"
      ? "bg-danger-soft hover:bg-danger-soft/80 border-danger border-l-2"
      : item.severity === "warning"
        ? "bg-warning-soft/60 hover:bg-warning-soft border-warning border-l-2"
        : "hover:bg-surface-overlay",
  );

  if (path) {
    return (
      <Link
        to={path}
        className={className}
        onClick={() => {
          onRead(item);
          onNavigate();
        }}
      >
        {content}
      </Link>
    );
  }

  return (
    <button type="button" className={className} onClick={() => onRead(item)}>
      {content}
    </button>
  );
}

/**
 * The banner a `critical` notice raises across the top of every page.
 *
 * It does not dismiss. The only way to clear it is to read the notice — which
 * is the point of the severity: a warning can wait for the next time someone
 * opens the inbox, and a critical one is not allowed to.
 */
export function CriticalNoticeBanner() {
  const { unreadItems } = useUnreadInbox();
  const { read } = useMarkRead();

  const notice = unreadItems.find((item) => item.severity === "critical");
  if (!notice) return null;

  const path = internalPath(notice.action_url);
  const others = unreadItems.filter((item) => item.severity === "critical").length - 1;

  return (
    <div
      role="alert"
      className="bg-danger-fill flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 text-white sm:px-6"
    >
      <AlertIcon className="size-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold wrap-anywhere">{notice.title}</p>
        <p className="line-clamp-2 text-xs opacity-90 wrap-anywhere">{notice.body}</p>
      </div>
      {others > 0 ? (
        <span className="text-xs opacity-80">
          +{others} more critical notice{others === 1 ? "" : "s"}
        </span>
      ) : null}
      <div className="flex shrink-0 items-center gap-2">
        {path ? (
          <Link
            to={path}
            onClick={() => read(notice)}
            className="rounded-control border border-white/60 px-2.5 py-1 text-xs font-medium hover:bg-white/10"
          >
            Open
          </Link>
        ) : null}
        <button
          type="button"
          onClick={() => read(notice)}
          className="rounded-control bg-white px-2.5 py-1 text-xs font-semibold text-black hover:bg-white/90"
        >
          Mark as read
        </button>
      </div>
    </div>
  );
}
